import React, { useContext, useMemo } from "react";
import { Box, Checkbox, Skeleton, Tooltip } from "@mui/material";
import ReleaseFlowContext from "../../context/releaseFlowContext";
import Switch from "../Switch/Switch";
import Textarea from "../Textarea/Textarea";
import { convertDatePtBr } from "../../utils/convertDatePtBr";
import {
  releaseFlowCheckboxSx,
  releaseFlowColumnCheckboxWrapperSx,
  releaseFlowColumnHeaderLabelSx,
  releaseFlowGridTemplateColumns,
  releaseFlowGridTemplateColumnsNoProduction,
  releaseFlowGridTemplateColumnsNoProductionNoHub,
  releaseFlowHeadingSx,
  releaseFlowInlineHeadingSx,
  releaseFlowTableSx,
} from "./ReleaseFlowStepDestinations";
import { ReleaseFlowVersionLabel } from "./ReleaseFlowVersionLabel/ReleaseFlowVersionLabel";

const observationHeadingSx = {
  ...releaseFlowHeadingSx,
  marginTop: "16px",
};

const switchesRowSx = {
  display: "flex",
  gap: "32px",
  marginBottom: "24px",
  "& .switchBlock": {
    display: "flex",
    alignItems: "center",
    gap: "0",
  },
  "& .switchContainer": {
    marginTop: 0,
  },
  "& .switchLabel": {
    display: "none",
  },
  "& .blockLabel": {
    margin: "0",
  },
};

const OBSERVATION_LABEL = {
  channel: "Canal",
  production: "Produção",
  cultureHub: "HUB Cultura",
};

const ALL_OBSERVATION_DESTINATIONS = ["channel", "production", "cultureHub"];

const NO_RELEASE_TOOLTIP_TEXT =
  "Não existem arquivos sendo liberados para esse local";

const ReleaseFlowStepVersions = () => {
  const {
    destinations,
    originalDestinations,
    oldVersions,
    keep,
    originalKeep,
    toggleKeepVersion,
    toggleKeepAllVersions,
    observationEnabled,
    observationText,
    setObservationEnabled,
    setObservationText,
    productionReleaseEnabled: TEXT_RELEASE_FEATURE_FOR_PRODUCTION_ENABLED,
    productionReleaseLoading,
    cultureHubReleaseEnabled: CULTURE_HUB_RELEASE_ENABLED,
    cultureHubReleaseLoading,
  } = useContext(ReleaseFlowContext);

  // Reserve the wider ("with production") layout while the flag is still
  // resolving so the column doesn't shift once for the skeleton and again
  // once the real value lands - it only narrows if production ends up
  // disabled.
  const showProduction =
    TEXT_RELEASE_FEATURE_FOR_PRODUCTION_ENABLED || productionReleaseLoading;
  const showCultureHub =
    CULTURE_HUB_RELEASE_ENABLED || cultureHubReleaseLoading;

  const gridTemplateColumns = showProduction && showCultureHub
    ? releaseFlowGridTemplateColumns
    : showProduction || showCultureHub
    ? releaseFlowGridTemplateColumnsNoProduction
    : releaseFlowGridTemplateColumnsNoProductionNoHub;

  // Feature flags required per destination (falsy entries are always shown).
  // Declarative allow-list instead of an all-or-nothing ternary array, so
  // adding a new gated destination later only means adding a row here.
  // Kept true while loading so the slot stays reserved for a skeleton below
  // instead of popping in once the flag resolves.
  const destinationFeatureFlags = {
    production:
      TEXT_RELEASE_FEATURE_FOR_PRODUCTION_ENABLED || productionReleaseLoading,
    cultureHub:
      CULTURE_HUB_RELEASE_ENABLED || cultureHubReleaseLoading,
  };

  const observationDestinations = ALL_OBSERVATION_DESTINATIONS.filter(
    (destination) => destinationFeatureFlags[destination] ?? true
  );

  const versionEntries = useMemo(
    () => Object.entries(oldVersions),
    [oldVersions]
  );

  const hasReleaseByDestination = useMemo(() => {
    const currentDestinationChanges = {
      channel: false,
      production: false,
      cultureHub: false,
    };

    Object.entries(destinations).forEach(([key, value]) => {
      const original = originalDestinations?.[key] || {};
      Object.keys(currentDestinationChanges).forEach((destination) => {
        if (
          Boolean(value[destination]) &&
          !Boolean(original[destination])
        ) {
          currentDestinationChanges[destination] = true;
        }
      });
    });

    Object.entries(keep).forEach(([key, value]) => {
      const original = originalKeep?.[key] || {};
      Object.keys(currentDestinationChanges).forEach((destination) => {
        if (
          Boolean(value[destination]) &&
          !Boolean(original[destination])
        ) {
          currentDestinationChanges[destination] = true;
        }
      });
    });

    return currentDestinationChanges;
  }, [destinations, keep, originalDestinations, originalKeep]);

  const {
    allChannelChecked,
    someChannelChecked,
    allCultureHubChecked,
    someCultureHubChecked,
    allProductionChecked,
    someProductionChecked,
  } = useMemo(() => {
    const values = versionEntries.map(([key]) => keep[key]).filter(Boolean);
    const channelCheckedCount = values.filter((v) => v.channel).length;
    const cultureHubCheckedCount = values.filter((v) => v.cultureHub).length;
    const productionCheckedCount = values.filter((v) => v.production).length;

    return {
      allChannelChecked:
        !!values.length && channelCheckedCount === values.length,
      someChannelChecked:
        channelCheckedCount > 0 && channelCheckedCount < values.length,
      allCultureHubChecked:
        !!values.length && cultureHubCheckedCount === values.length,
      someCultureHubChecked:
        cultureHubCheckedCount > 0 && cultureHubCheckedCount < values.length,
      allProductionChecked:
        !!values.length && productionCheckedCount === values.length,
      someProductionChecked:
        productionCheckedCount > 0 && productionCheckedCount < values.length,
    };
  }, [versionEntries, keep]);

  return (
    <Box>
      {!!versionEntries.length && (
        <>
          <Box sx={{ ...releaseFlowTableSx.headerRow, gridTemplateColumns }}>
            <Box sx={releaseFlowInlineHeadingSx}>
              Manter demais versões liberadas?
            </Box>
            <Box sx={releaseFlowColumnHeaderLabelSx}>
              <Checkbox
                checked={allChannelChecked}
                indeterminate={someChannelChecked}
                onChange={(ev) =>
                  toggleKeepAllVersions("channel", ev.target.checked)
                }
                size="small"
                sx={releaseFlowCheckboxSx}
              />
              <Box component="span">Canal</Box>
            </Box>
            {(CULTURE_HUB_RELEASE_ENABLED || cultureHubReleaseLoading) && (
              <Box sx={releaseFlowColumnHeaderLabelSx}>
                {cultureHubReleaseLoading ? (
                  <Skeleton
                    variant="rounded"
                    width={90}
                    height={24}
                    animation="wave"
                  />
                ) : (
                  <>
                    <Checkbox
                      checked={allCultureHubChecked}
                      indeterminate={someCultureHubChecked}
                      onChange={(ev) =>
                        toggleKeepAllVersions("cultureHub", ev.target.checked)
                      }
                      size="small"
                      sx={releaseFlowCheckboxSx}
                    />
                    <Box component="span">HUB Cultura</Box>
                  </>
                )}
              </Box>
            )}
            {(TEXT_RELEASE_FEATURE_FOR_PRODUCTION_ENABLED ||
              productionReleaseLoading) && (
              <Box sx={releaseFlowColumnHeaderLabelSx}>
                {productionReleaseLoading ? (
                  <Skeleton
                    variant="rounded"
                    width={90}
                    height={24}
                    animation="wave"
                  />
                ) : (
                  <>
                    <Checkbox
                      checked={allProductionChecked}
                      indeterminate={someProductionChecked}
                      onChange={(ev) =>
                        toggleKeepAllVersions("production", ev.target.checked)
                      }
                      size="small"
                      sx={releaseFlowCheckboxSx}
                    />
                    <Box component="span">Produção</Box>
                  </>
                )}
              </Box>
            )}
          </Box>

          <Box sx={releaseFlowTableSx.card}>
            {versionEntries.map(([key, version]) => {
              const rowKeep = keep[key] || {};

              return (
                <Box
                  key={key}
                  sx={{ ...releaseFlowTableSx.row, gridTemplateColumns }}
                >
                  <Box>{version.fileName}</Box>
                  <Box>
                    <ReleaseFlowVersionLabel attachment={version} />
                  </Box>
                  <Box>
                    {convertDatePtBr(
                      version?.deliveryControl?.completionDates?.received
                    )}
                  </Box>
                  <Box sx={releaseFlowColumnCheckboxWrapperSx}>
                    <Checkbox
                      checked={!!rowKeep.channel}
                      onChange={() => toggleKeepVersion(key, "channel")}
                      size="small"
                      sx={releaseFlowCheckboxSx}
                    />
                  </Box>
                  {(CULTURE_HUB_RELEASE_ENABLED || cultureHubReleaseLoading) && (
                    <Box sx={releaseFlowColumnCheckboxWrapperSx}>
                      <Checkbox
                        checked={!!rowKeep.cultureHub}
                        onChange={() => toggleKeepVersion(key, "cultureHub")}
                        size="small"
                        sx={releaseFlowCheckboxSx}
                      />
                    </Box>
                  )}
                  {productionReleaseLoading ? (
                    <Box sx={releaseFlowColumnCheckboxWrapperSx}>
                      <Skeleton
                        variant="rounded"
                        width={20}
                        height={20}
                        animation="wave"
                      />
                    </Box>
                  ) : (
                    TEXT_RELEASE_FEATURE_FOR_PRODUCTION_ENABLED && (
                      <Box sx={releaseFlowColumnCheckboxWrapperSx}>
                        <Checkbox
                          checked={!!rowKeep.production}
                          onChange={() => toggleKeepVersion(key, "production")}
                          size="small"
                          sx={releaseFlowCheckboxSx}
                        />
                      </Box>
                    )
                  )}
                </Box>
              );
            })}
          </Box>
        </>
      )}

      <Box sx={observationHeadingSx}>Adicionar observação (opcional)</Box>

      <Box sx={switchesRowSx}>
        {observationDestinations.map((destination) => {
          if (destination === "production" && productionReleaseLoading) {
            return (
              <Skeleton
                key={destination}
                variant="rounded"
                width={110}
                height={24}
                animation="wave"
              />
            );
          }

          const disabled = !hasReleaseByDestination[destination];
          const switchEl = (
            <Switch
              blockLabel={OBSERVATION_LABEL[destination]}
              switchOn={observationEnabled[destination]}
              disabled={disabled}
              event={() =>
                setObservationEnabled(
                  destination,
                  !observationEnabled[destination]
                )
              }
            />
          );

          if (!disabled) {
            return (
              <React.Fragment key={destination}>{switchEl}</React.Fragment>
            );
          }

          return (
            <Tooltip key={destination} title={NO_RELEASE_TOOLTIP_TEXT} arrow>
              <Box>{switchEl}</Box>
            </Tooltip>
          );
        })}
      </Box>

      {observationDestinations.map(
        (destination) =>
          observationEnabled[destination] && (
            <Box key={destination} sx={{ marginBottom: "16px" }}>
              <Textarea
                maxRows={12}
                label={`Observação para ${OBSERVATION_LABEL[destination]}`}
                placeholder="Escreva aqui as suas observações sobre a liberação dos textos"
                handleChange={(ev) =>
                  setObservationText(destination, ev.target.value)
                }
                value={observationText[destination]}
              />
            </Box>
          )
      )}
    </Box>
  );
};

export default ReleaseFlowStepVersions;
